/* @jsxImportSource @emotion/react */
import { css } from '@emotion/react/macro'
import React, { useCallback, useMemo } from 'react'
import { Modal, Table } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import { CloseOutlined } from '@ant-design/icons'

interface PlayerListModalProps {
  roster: any
  isVisible: boolean
  onClose: () => void
  modalTitle?: string
}

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10002,
  },
  mask: {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10001,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

const renderPlayerTable = (roster: any) => {
  if (!roster?.players?.length) {
    return null
  }

  const columns = [
    {
      title: '#',
      dataIndex: 'player_number',
      key: 'player_number',
      width: 24,
      sorter: (a: any, b: any) =>
        (a.player_number || 0) - (b.player_number || 0),
    },
    {
      title: 'Name',
      key: 'name',
      render: (player: any) => `${player.name_first} ${player.name_last}`,
      sorter: (a: any, b: any) => {
        const nameA = `${a.name_first} ${a.name_last}`.toLowerCase()
        const nameB = `${b.name_first} ${b.name_last}`.toLowerCase()
        return nameA.localeCompare(nameB)
      },
    },
    {
      title: 'Position',
      key: 'position',
      render: (player: any) => player.player_type || '-',
      sorter: (a: any, b: any) => {
        const posA = a.player_type || ''
        const posB = b.player_type || ''
        return posA.localeCompare(posB)
      },
    },
    {
      title: 'Customer ID',
      key: 'customer_identifier',
      dataIndex: 'customer_identifier',
      render: (value: string) => value || '-',
      width: 120,
      sorter: (a: any, b: any) => {
        const idA = a.customer_identifier || ''
        const idB = b.customer_identifier || ''
        return idA.localeCompare(idB)
      },
    },
    // {
    //   title: 'Affiliate',
    //   key: 'is_affiliate',
    //   width: 80,
    //   align: 'center' as const,
    //   render: (player: any) =>
    //     player.is_affiliate ? (
    //       <CheckCircleFilled
    //         css={css`
    //           color: ${colors.SUCCESS_300};
    //           font-size: 16px;
    //         `}
    //       />
    //     ) : null,
    // },
  ]

  return (
    <div
      css={css`
        flex: 1;
        overflow: auto;
      `}
    >
      <Table
        dataSource={roster?.players}
        columns={columns}
        pagination={{
          pageSize: 15,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
          showSizeChanger: false,
          showQuickJumper: false,
        }}
        size='small'
        bordered={true}
        style={{
          width: '100%',
          maxHeight: '80vh',
          overflow: 'auto',
        }}
        rowClassName={(_, index) => (index % 2 === 0 ? 'even-row' : 'odd-row')}
        css={css`
          .even-row {
            background-color: ${colors.SECONDARY_400};
          }
          .odd-row {
            background-color: ${colors.SECONDARY_500};
          }
        `}
      />
    </div>
  )
}

export const PlayerListModal: React.FC<PlayerListModalProps> = ({
  roster,
  isVisible,
  onClose,
  modalTitle,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])

  const getHeader = () => (
    <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
      {modalTitle || `Players - ${roster?.name || ''}`}
    </Text>
  )

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 16px;
        `}
      />
    )
  }, [])

  return (
    <Modal
      title={getHeader()}
      closeIcon={closeIcon()}
      open={isVisible}
      onCancel={onClose}
      footer={null}
      styles={modalStyles}
      centered
      css={css`
        display: flex;
        flex: 1;
        max-height: ${isMobile ? '100vh' : '90vh'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '60vw'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          max-height: ${isMobile ? '100vh' : '90vh'} !important;
          overflow: hidden;
          border-radius: ${isMobile ? '0' : '8px'};
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
          background: ${colors.SECONDARY_300};
        }
      `}
      width={isMobile ? '100%' : '50vw'}
    >
      {renderPlayerTable(roster)}
    </Modal>
  )
}
